import { apiGet } from './PlanningApiClient';
import { fromApiEvent, type StoredEvent } from './EventsService';
import { toIsoDate } from '../utils/eventDates';

export interface AccountCalendarEntry {
  /** Day the event happens, "YYYY-MM-DD". */
  day: string;
  event: StoredEvent;
  projectId: string;
  projectName: string;
}

export interface AccountCalendarResult {
  entries: AccountCalendarEntry[];
  /** Projects whose events could not be read (the rest are still shown). */
  failed: string[];
}

/**
 * Every project's active events in one list, for the account Calendar. Each
 * project is asked on its own URL, so a project the person cannot read (or that
 * is down) is reported by name and the others still show.
 */
export async function loadAccountCalendar(projects: Array<{ project_id: string; project_name: string }>): Promise<AccountCalendarResult> {
  const settled = await Promise.allSettled(
    projects.map(async p => {
      const raw = await apiGet<{ events?: Record<string, unknown>[] }>(`/planning/${encodeURIComponent(p.project_id)}/events`);
      return (raw?.events ?? []).map(fromApiEvent).map<AccountCalendarEntry>(event => ({
        day: toIsoDate(event.date),
        event,
        projectId: p.project_id,
        projectName: p.project_name,
      }));
    }),
  );
  const entries: AccountCalendarEntry[] = [];
  const failed: string[] = [];
  settled.forEach((result, i) => {
    if (result.status === 'fulfilled') entries.push(...result.value.filter(e => e.day));
    else failed.push(projects[i].project_name);
  });
  entries.sort((a, b) => a.day.localeCompare(b.day) || (a.event.startTime ?? '').localeCompare(b.event.startTime ?? '') || a.projectName.localeCompare(b.projectName));
  return { entries, failed };
}

/** Entries that fall in the month "YYYY-MM". */
export function entriesInMonth(entries: AccountCalendarEntry[], month: string): AccountCalendarEntry[] {
  return entries.filter(e => e.day.startsWith(`${month}-`));
}
